// TRACK C — corrected LCOE for the REPORT LCOE surfaces.
//
// SINGLE SOURCE OF TRUTH for the levelized-cost figures shown in the report
// renderers (the BTM overview table in SiteRagReportView and the LcoeChartSection
// bar chart). Both surfaces previously baked in HARDCODED literature bands
// (nuclear 65–100, gas 45–75, solar 40–80, wind 35–90 $/MWh) plus a crude
// land-area heuristic. Those bands are NOT the project's actual LCOE: they
// ignore the load→nameplate overbuild that firming a flat 24/7 datacenter load
// on a low-capacity-factor source requires (a solar+BESS build sized to SERVE
// the load is materially more expensive than its bare generation band), and
// they ignore the project's WACC / phasing / COD.
//
// This module resolves the AUTHORITATIVE per-technology LCOE for a report:
//
//   1. PERSISTED — `report.pathwayLcoeAtGen` (stamped by Track B at generation
//      time). Preferred for reproducibility: a saved report shows the figure
//      that was frozen in, not a recomputation against possibly-drifted libs.
//   2. COMPUTED LIVE — from the gen-time project intent. We build intent-FAITHFUL
//      pathways (`pathwaysFromIntentPhases`), which already gross the facility
//      load up to the required generation NAMEPLATE via
//      `resolveGenerationNameplateForTech` (the load→nameplate source of truth),
//      then take each pathway's WACC-discounted real LCOE from
//      `computePathwayLcoe`. No band is fabricated.
//   3. PENDING — when neither a persisted figure nor an intent is available, the
//      surface degrades to `null` (rendered as "pending"), NOT a fabricated band.
//
// UNITS: USD per MWh throughout. Pure financial arithmetic — no CoolProp /
// IAPWS boundary here. The corrected LCOE comes straight from the validated
// finDecision cashflow engine; this module only ROUTES + GROUPS it.

import type { ProjectIntent, SiteRagReport } from '../../types/siteRag';
import type { SiteContext } from '../../types/siteContext';
import { emptySiteContext } from '../../types/siteContext';
import type { LcoeBasis } from '../lcoeBasis';
import type { Pathway, TechnologyId } from './types';
import { pathwaysFromIntentPhases } from './intentPosture';
import { computePathwayLcoe } from './pathwayLcoe';

/**
 * Coarse technology BUCKET used to match the report's free-text BTM technology
 * labels (e.g. "Nuclear SMR", "Natural Gas", "Solar + BESS") and the fixed
 * chart rows. The finance presets are finer-grained (`gas-cc` vs `gas-sc`,
 * `nuclear-smr` vs `nuclear-large`); we collapse to the bucket the surfaces
 * actually render so a single corrected figure backs each visible row.
 */
export type LcoeTechBucket = 'nuclear' | 'gas' | 'solar' | 'wind' | 'grid' | 'mixed';

/** Where a corrected LCOE figure came from — drives "pending" vs a real number. */
export type LcoeSurfaceSource = 'persisted' | 'computed' | 'pending';

/** One corrected LCOE entry for a technology bucket. */
export interface CorrectedLcoeEntry {
  /** WACC-discounted real LCOE midpoint, USD/MWh. `null` ⇒ pending (no source). */
  midUsdPerMwh: number | null;
  source: LcoeSurfaceSource;
  /**
   * ALWAYS `'benchmark-pathway'` (audit Q6 / 4.4). This surface prices what a
   * technology OPTION of this size would cost; it is NOT this project's own
   * project-finance LCOE, which is the report headline. Carried per entry so a
   * renderer cannot show one of these beside a headline figure without saying
   * they answer different questions.
   */
  basis: LcoeBasis;
}

export interface CorrectedLcoeSurface {
  /** Per-bucket corrected LCOE. Buckets absent from the intent map to pending. */
  byBucket: Record<LcoeTechBucket, CorrectedLcoeEntry>;
  /** Provenance of the whole surface (worst-case: pending if nothing resolved). */
  source: LcoeSurfaceSource;
  /** True when at least one bucket carries a real (non-null) corrected figure. */
  hasAnyCorrected: boolean;
}

/** Map a finance `TechnologyId` to the coarse render bucket. */
export function technologyIdToBucket(tech: TechnologyId): LcoeTechBucket {
  switch (tech) {
    case 'nuclear-smr':
    case 'nuclear-large':
      return 'nuclear';
    case 'gas-cc':
    case 'gas-sc':
      return 'gas';
    case 'solar-bess':
      return 'solar';
    case 'wind-bess':
      return 'wind';
    case 'grid-tariff':
      return 'grid';
    case 'mixed':
      return 'mixed';
    default: {
      // Exhaustiveness guard — a new TechnologyId must be bucketed explicitly.
      const _never: never = tech;
      void _never;
      return 'mixed';
    }
  }
}

/**
 * Match a report's free-text BTM technology label (e.g. "Nuclear SMR",
 * "Natural Gas Reciprocating", "Solar + BESS", "Onshore Wind") to a render
 * bucket. Returns `null` when no bucket is recognised (the surface then shows
 * the existing authored value / dash rather than a wrong corrected figure).
 */
export function technologyLabelToBucket(label: string | undefined | null): LcoeTechBucket | null {
  if (!label) return null;
  const t = label.toLowerCase();
  if (t.includes('nuclear') || t.includes('smr')) return 'nuclear';
  if (t.includes('gas')) return 'gas';
  if (t.includes('solar')) return 'solar';
  if (t.includes('wind')) return 'wind';
  if (t.includes('grid')) return 'grid';
  return null;
}

/** A pending entry — no source resolved. */
function pendingEntry(): CorrectedLcoeEntry {
  return { midUsdPerMwh: null, source: 'pending', basis: 'benchmark-pathway' };
}

function emptyByBucket(): Record<LcoeTechBucket, CorrectedLcoeEntry> {
  return {
    nuclear: pendingEntry(),
    gas: pendingEntry(),
    solar: pendingEntry(),
    wind: pendingEntry(),
    grid: pendingEntry(),
    mixed: pendingEntry(),
  };
}

/**
 * Loose shape of `report.pathwayLcoeAtGen` (stamped by Track B). The field is
 * not yet on the `SiteRagReport` type, so we read it defensively: any of a
 * `TechnologyId`-keyed or bucket-keyed record of `{ lcoeRealPerMwhUsd }` /
 * `{ midUsdPerMwh }` / a bare number is accepted. Forward-compatible with Track
 * B's chosen shape without coupling this module to it.
 */
type PersistedLcoeValue =
  | number
  | { lcoeRealPerMwhUsd?: number; lcoeNominalPerMwhUsd?: number; midUsdPerMwh?: number }
  | null
  | undefined;

function coercePersistedValue(v: PersistedLcoeValue): number | null {
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  if (v && typeof v === 'object') {
    const candidate = v.lcoeRealPerMwhUsd ?? v.midUsdPerMwh ?? v.lcoeNominalPerMwhUsd;
    if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0) {
      return candidate;
    }
  }
  return null;
}

/**
 * One persisted pathway-LCOE row as written by Track B's
 * `buildPathwayLcoeAtGenSnapshot` (the AUTHORITATIVE persisted shape). The
 * surface is stamped as an ARRAY of these — bucketed here by the `technology`
 * field, NOT by the array index.
 */
interface PersistedLcoeArrayRow {
  technology?: string;
  lcoeRealPerMwhUsd?: number;
  lcoeNominalPerMwhUsd?: number;
  midUsdPerMwh?: number;
}

/**
 * Fold one (bucket, midpoint) pair into the accumulating surface, keeping the
 * lowest (most representative) figure when several techs collapse to one
 * bucket — matches the live-compute aggregation. Returns true when a real
 * figure was recorded.
 */
function recordBucket(
  byBucket: Record<LcoeTechBucket, CorrectedLcoeEntry>,
  bucket: LcoeTechBucket | null,
  mid: number | null,
): boolean {
  if (!bucket || mid == null) return false;
  const existing = byBucket[bucket].midUsdPerMwh;
  byBucket[bucket] = {
    midUsdPerMwh: existing == null ? mid : Math.min(existing, mid),
    source: 'persisted',
    basis: 'benchmark-pathway',
  };
  return true;
}

/**
 * Read a persisted `pathwayLcoeAtGen` surface off the report, if Track B stamped
 * one. Accepts BOTH stamped shapes:
 *   - the AUTHORITATIVE Track-B ARRAY (`buildPathwayLcoeAtGenSnapshot` →
 *     `Array<{ technology, lcoeRealPerMwhUsd, … }>`), bucketed by each row's
 *     `technology` field; and
 *   - a `TechnologyId`-keyed or bucket-keyed RECORD (legacy / forward-compat).
 * Returns null when absent / unusable so the caller falls through to the live
 * compute. Reading an array via `Object.entries` would bucket by the numeric
 * index (never a valid tech/bucket key) and silently drop every row — so the
 * array shape MUST be handled explicitly.
 */
function readPersistedSurface(
  report: SiteRagReport,
): Record<LcoeTechBucket, CorrectedLcoeEntry> | null {
  // The field is read loosely so this module stays decoupled from Track B's
  // exact shape (array OR record).
  const raw = (report as unknown as {
    pathwayLcoeAtGen?: PersistedLcoeArrayRow[] | Record<string, PersistedLcoeValue>;
  }).pathwayLcoeAtGen;
  if (!raw || typeof raw !== 'object') return null;

  const byBucket = emptyByBucket();
  let any = false;

  if (Array.isArray(raw)) {
    // AUTHORITATIVE Track-B shape — bucket by the row's `technology`, not index.
    for (const row of raw) {
      if (!row || typeof row !== 'object') continue;
      const mid = coercePersistedValue(row);
      const bucket = typeof row.technology === 'string'
        ? normaliseKeyToBucket(row.technology)
        : null;
      if (recordBucket(byBucket, bucket, mid)) any = true;
    }
    return any ? byBucket : null;
  }

  // RECORD shape — keys may be TechnologyIds or buckets.
  for (const [key, value] of Object.entries(raw)) {
    const mid = coercePersistedValue(value);
    const bucket = normaliseKeyToBucket(key);
    if (recordBucket(byBucket, bucket, mid)) any = true;
  }
  return any ? byBucket : null;
}

const TECHNOLOGY_IDS: ReadonlySet<string> = new Set([
  'gas-cc',
  'gas-sc',
  'nuclear-smr',
  'nuclear-large',
  'solar-bess',
  'wind-bess',
  'grid-tariff',
  'mixed',
]);

const BUCKETS: ReadonlySet<string> = new Set([
  'nuclear',
  'gas',
  'solar',
  'wind',
  'grid',
  'mixed',
]);

function normaliseKeyToBucket(key: string): LcoeTechBucket | null {
  if (BUCKETS.has(key)) return key as LcoeTechBucket;
  if (TECHNOLOGY_IDS.has(key)) return technologyIdToBucket(key as TechnologyId);
  // Fall back to a label match (defensive — Track B may key by display label).
  return technologyLabelToBucket(key);
}

/**
 * Resolve the project intent to compute against: the gen-time snapshot
 * (`projectIntentAtGen`, the reproducibility invariant) is preferred; a loose
 * `projectIntent` field (used by some renderers) is the fallback.
 */
function resolveIntentForReport(report: SiteRagReport): ProjectIntent | undefined {
  if (report.projectIntentAtGen) return report.projectIntentAtGen;
  const loose = (report as unknown as { projectIntent?: ProjectIntent }).projectIntent;
  return loose ?? undefined;
}

/**
 * Compute the corrected per-bucket LCOE live from the report's intent. Builds
 * intent-faithful pathways (already load→nameplate sized) and takes each
 * pathway's WACC-discounted real LCOE, grouping to the render bucket and
 * keeping the lowest (most representative) figure per bucket. Returns null when
 * there is no usable intent / no resolvable pathway LCOE.
 */
function computeLiveSurface(
  report: SiteRagReport,
  now: Date,
): Record<LcoeTechBucket, CorrectedLcoeEntry> | null {
  const intent = resolveIntentForReport(report);
  if (!intent) return null;

  const siteContext: SiteContext =
    report.siteContextAtGen ??
    (report as unknown as { siteContext?: SiteContext }).siteContext ??
    emptySiteContext();

  let pathways: Pathway[];
  try {
    pathways = pathwaysFromIntentPhases(intent, siteContext, now);
  } catch {
    return null;
  }
  if (pathways.length === 0) return null;

  const byBucket = emptyByBucket();
  let any = false;
  for (const pathway of pathways) {
    const { lcoeRealPerMwhUsd } = computePathwayLcoe(pathway, { now });
    if (!Number.isFinite(lcoeRealPerMwhUsd) || lcoeRealPerMwhUsd <= 0) continue;
    const bucket = technologyIdToBucket(pathway.technology);
    const existing = byBucket[bucket].midUsdPerMwh;
    byBucket[bucket] = {
      midUsdPerMwh: existing == null ? lcoeRealPerMwhUsd : Math.min(existing, lcoeRealPerMwhUsd),
      source: 'computed',
      basis: 'benchmark-pathway',
    };
    any = true;
  }
  return any ? byBucket : null;
}

/**
 * Resolve the corrected LCOE surface for a report — the SINGLE source both the
 * BTM overview table and the LCOE chart read.
 *
 * Precedence: persisted `pathwayLcoeAtGen` (Track B) → live compute from the
 * gen-time intent → pending. Pending buckets carry `null` so the renderers show
 * "pending", never a fabricated literature band.
 */
export function resolveReportCorrectedLcoe(
  report: SiteRagReport,
  opts?: { now?: Date },
): CorrectedLcoeSurface {
  const now = opts?.now ?? new Date();

  const persisted = readPersistedSurface(report);
  if (persisted) {
    return {
      byBucket: persisted,
      source: 'persisted',
      hasAnyCorrected: true,
    };
  }

  const live = computeLiveSurface(report, now);
  if (live) {
    return {
      byBucket: live,
      source: 'computed',
      hasAnyCorrected: true,
    };
  }

  return {
    byBucket: emptyByBucket(),
    source: 'pending',
    hasAnyCorrected: false,
  };
}

/**
 * Corrected LCOE for a single render bucket, or pending when unavailable.
 * Convenience for the chart rows / table cells which key by bucket.
 */
export function correctedLcoeForBucket(
  surface: CorrectedLcoeSurface,
  bucket: LcoeTechBucket | null,
): CorrectedLcoeEntry {
  if (!bucket) return pendingEntry();
  return surface.byBucket[bucket];
}

/**
 * Part C SP4 (S2) — the WORST (highest) corrected $/MWh across the surface's
 * resolved buckets. This is the figure the FINANCIAL PILLAR scores against:
 * the costliest path the site would actually build, drawn from the SAME
 * canonical corrected-LCOE surface the report renders (BTM table + chart), so
 * the pillar score and the prose/chart can never quote divergent $/MWh.
 *
 * Each `byBucket` entry already holds the LOWEST figure per technology bucket
 * (most-representative per tech). Taking the MAX across those bucket figures
 * yields "worst-of-the-min-per-bucket": the highest LCOE among the technologies
 * the site is comparing. Returns `null` when no bucket carries a real figure
 * (surface pending) so the caller can fall through to its legacy path.
 */
export function worstCorrectedLcoe(surface: CorrectedLcoeSurface): number | null {
  let worst: number | null = null;
  for (const bucket of Object.values(surface.byBucket)) {
    const v = bucket.midUsdPerMwh;
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
      if (worst == null || v > worst) worst = v;
    }
  }
  return worst;
}
